import React, {forwardRef, memo, useEffect, useRef} from 'react';
import Select, {CSSObjectWithLabel, OptionProps, components} from 'react-select';
import cn from 'classnames';
import {Controller, useFormContext} from 'react-hook-form';
import LinearText from './../linear-text/linear-text';

type Option = {
    label: string;
    value: string | number;
};
interface IProps {
    value?: any;
    label?: string;
    classNameLabel?: string;
    onSelectionChange?: (option: any) => void;
    options: Option[];
    className?: string;
    placeHolder?: string;
    isDisable?: boolean;
    name: string;
    defaultValue?: any;
    ref?: any;
}

const selectStyles = {
    control: (baseStyles: CSSObjectWithLabel) => ({
        ...baseStyles,
        border: 'none',
        backgroundColor: '#FFF',
        margin: 'auto',
        padding: 0,
        outline: 'none',
        boxShadow: 'none',
        minHeight: 0,
        height: '100%',
        width: '100%',
    }),
    option: (provided: CSSObjectWithLabel) => ({
        ...provided,
        backgroundColor: 'white',
        cursor: 'pointer',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 10px',
    }),
    singleValue: (provided: CSSObjectWithLabel) => ({
        ...provided,
        color: '#2722FB',
        fontSize: '14px',
    }),
    indicatorSeparator: (baseStyles: CSSObjectWithLabel) => ({
        ...baseStyles,
        backgroundColor: 'none',
        margin: 'auto',
    }),
    dropdownIndicator: (baseStyles: CSSObjectWithLabel) => ({
        ...baseStyles,
        color: 'rgba(39, 34, 251, 1)',
        margin: 'auto',
    }),
    menuPortal: (baseStyles: CSSObjectWithLabel) => ({
        ...baseStyles,
        zIndex: 10,
    }),
};

const classes = {
    root: ' w-full md:text-[14px] text-[12px] rounded-[8px] h-[40px] px-[4px] md:gap-[10px] bg-[#fff] !border-none !outline-none',
    rootLabel: 'md:text-[14px] text-[12px]',
    container: 'bg-clip relative flex flex-col rounded-[8px] bg-festival-color_divider bg-gradient-border p-[1px]',
};

// Custom SelectedOption
const SelectedOption: React.FC<OptionProps<Option, false>> = props => {
    const isSelected = props.isSelected;
    return (
        <components.Option {...props}>
            {isSelected ? (
                <LinearText as={'span'} className="font-bold">
                    {props.label}
                </LinearText>
            ) : (
                <span>{props.label}</span>
            )}
        </components.Option>
    );
};

// Custom single value with gradient text
const GradientSingleValue: React.FC<any> = props => {
    return (
        <components.SingleValue {...props}>
            <LinearText as={'span'} className='font-bold'>{props.data.label}</LinearText>
        </components.SingleValue>
    );
};

const SelectGradient: React.FC<IProps> = forwardRef(
    (
        {
            name,
            isDisable,
            label,
            classNameLabel,
            className,
            placeHolder,
            options,
            onSelectionChange,
            value: propsValue,
            defaultValue,
        },
        ref,
    ) => {
        const labelStyle = cn(classes.rootLabel, classNameLabel);
        const selectStyle = cn(classes.root, className);
        const containerStyle = cn(classes.container, {
            'opacity-50': isDisable,
        });

        const {control} = useFormContext();
        const onChangeRef = useRef<any>(null);

        useEffect(() => {
            if (typeof onChangeRef.current === 'function') {
                onChangeRef.current(propsValue);
            }
        }, [propsValue]);
        
        return (
            <>
                {label && (
                    <span className="">
                        <label className={labelStyle}>{label}</label>
                    </span>
                )}
                <div data-testid="select-gradient" className={containerStyle}>
                    <Controller
                        control={control}
                        name={name}
                        render={({field: {onChange, onBlur, ref}}) => {
                            onChangeRef.current = onChange;
                            const handleOnChange = (data: any) => {
                                onChange(data);
                                onSelectionChange && onSelectionChange(data);
                            };
                            return (
                                <Select
                                    onChange={handleOnChange}
                                    onBlur={onBlur}
                                    className={selectStyle}
                                    classNamePrefix="border-0 custom-select"
                                    options={options}
                                    styles={selectStyles}
                                    ref={ref}
                                    placeholder={placeHolder}
                                    value={propsValue}
                                    defaultValue={defaultValue}
                                    isDisabled={isDisable}
                                    menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
                                    components={{
                                        Option: SelectedOption,
                                        SingleValue: GradientSingleValue
                                    }}
                                />
                            );
                        }}
                    />
                </div>
            </>
        );
    },
);

SelectGradient.displayName = 'SelectGradient';

export default memo(SelectGradient);
